import { ChangeDetectionStrategy, Component, Input, OnChanges } from "@angular/core";
import {
  PanelEquipmentCounters,
  PanelGatewayInfo,
  PanelProgress,
  PanelQueueSummary
} from "../../interfaces/panel-alerts.interface";

/**
 * Pie del panel de testeos: gateway en uso, progreso de envio de comandos y
 * reparto de equipos entre respondidos, en espera y con error.
 */
@Component({
  selector: "app-panel-footer",
  templateUrl: "./footer.html",
  styleUrls: ["./footer.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PanelFooterComponent implements OnChanges {
  @Input() gateways: PanelGatewayInfo[] = [];
  /** Gateway que respondio la ultima trama. */
  @Input() activeGatewayId: number = null;
  @Input() progress: PanelProgress = { sent: 0, total: 0, percentage: 0 };
  @Input() counters: PanelEquipmentCounters = { answered: 0, waiting: 0, with_error: 0 };
  /** Equipos en espera cuyo frame sigue en la cola del backend o ya salio de ella. */
  @Input() queue: PanelQueueSummary = { waiting: 0, sent: 0 };

  /** Gateway que se muestra, resuelto a partir de las tramas recibidas. */
  gateway: PanelGatewayInfo = null;
  /** Clave i18n del estado del gateway. */
  gatewayStatusKey = "panel-alerts.gateway-disconnected";
  /** Clave i18n del texto corto (ON/OFF) de la pildora del gateway. */
  gatewayPillKey = "panel-alerts.gateway-off";
  /** Gateways del lote (con equipos), los caidos primero; para el popover. */
  lotGateways: PanelGatewayInfo[] = [];
  /** El lote usa mas de un gateway: el pie muestra el resumen en lugar de uno solo. */
  multiGateway = false;
  /** Gateways del lote conectados. */
  onlineCount = 0;
  /** Gateways del lote desconectados. */
  offlineCount = 0;

  /**
   * Resuelve el gateway a mostrar y la clave de su estado.
   * @returns void
   */
  ngOnChanges(): void {
    this.gateway = this.resolveGateway();
    const connected = !!(this.gateway && this.gateway.connected);
    this.gatewayStatusKey = connected
      ? "panel-alerts.gateway-connected"
      : "panel-alerts.gateway-disconnected";
    this.gatewayPillKey = connected ? "panel-alerts.gateway-on" : "panel-alerts.gateway-off";

    /* Solo se resume cuando se sabe que equipos usan cada gateway (vienen de
       los propios equipos); los de la granja no dicen cuales usa el lote. */
    const lot = this.gateways.filter((gateway) => (gateway.feeders || 0) > 0);
    this.multiGateway = lot.length > 1;
    this.onlineCount = lot.filter((gateway) => gateway.connected).length;
    this.offlineCount = lot.length - this.onlineCount;
    this.lotGateways = [
      ...lot.filter((gateway) => !gateway.connected),
      ...lot.filter((gateway) => gateway.connected)
    ];
  }

  /**
   * Elige que gateway mostrar.
   *
   * Una granja puede tener varios, asi que se prefiere el que respondio la
   * ultima trama; si aun no ha llegado ninguna, el primero que este conectado.
   * @returns Gateway a mostrar, o `null` si no se conoce ninguno.
   */
  private resolveGateway(): PanelGatewayInfo {
    if (this.gateways.length === 0) {
      return null;
    }
    const active = this.gateways.find((gateway) => gateway.id === this.activeGatewayId);
    if (active) {
      return active;
    }
    const connected = this.gateways.find((gateway) => gateway.connected);
    return connected ? connected : this.gateways[0];
  }
}
